import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Reads the global "tiers paused" switch from site_settings.
 * While paused, tier ranks and tier-boosted daily bonuses are disabled
 * and every account is treated as unranked.
 */
export function useTiersPaused() {
  const [paused, setPaused] = useState(false);
  const [resumeDate, setResumeDate] = useState<string>("September 1");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const formatDate = (raw: string) => {
      const d = new Date(`${raw}T00:00:00Z`);
      if (isNaN(d.getTime())) return raw;
      return d.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        timeZone: "UTC",
      });
    };

    const load = async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", ["tiers_paused", "tiers_paused_until"]);
      if (!active) return;
      const map = Object.fromEntries((data ?? []).map((r) => [r.key, r.value]));
      setPaused(map.tiers_paused === "true");
      if (map.tiers_paused_until) setResumeDate(formatDate(map.tiers_paused_until));
      setLoading(false);
    };

    load();

    const channel = supabase
      .channel("tiers-paused-settings")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "site_settings" },
        (payload) => {
          const row = payload.new as { key: string; value: string };
          if (row.key === "tiers_paused") setPaused(row.value === "true");
          if (row.key === "tiers_paused_until") setResumeDate(formatDate(row.value));
        }
      )
      .subscribe();

    return () => {
      active = false;
      supabase.removeChannel(channel);
    };
  }, []);

  return { paused, resumeDate, loading };
}

export default useTiersPaused;